import { useRef, useState } from 'react';
import { Draggable } from '@carbon/react/icons';
import styles from './reorderable-list.module.scss';

interface ReorderableListProps {
  items: string[];
  onReorder: (items: string[]) => void;
  disabled?: boolean;
}

/**
 * A ranked list that supports drag-to-reorder via native HTML drag-and-drop.
 * Each row shows a grip handle, rank number, and item label.
 * @param {ReorderableListProps} props - The component props
 * @return {JSX.Element} The reorderable list
 */
export function ReorderableList({
  items,
  onReorder,
  disabled = false,
}: ReorderableListProps): JSX.Element {
  const dragIndex = useRef<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  /**
   * Stores the index of the item being dragged.
   * @param {number} i - The index of the dragged item
   */
  const handleDragStart = (i: number) => {
    dragIndex.current = i;
  };

  /**
   * Tracks which item is being dragged over for visual feedback.
   * @param {React.DragEvent} e - The drag event
   * @param {number} i - The index of the item being dragged over
   */
  const handleDragOver = (e: React.DragEvent, i: number) => {
    e.preventDefault();
    setDragOverIndex(i);
  };

  /**
   * Reorders the list by moving the dragged item to the drop target position.
   * @param {number} i - The index of the drop target
   */
  const handleDrop = (i: number) => {
    const from = dragIndex.current;
    if (from === null || from === i) {
      setDragOverIndex(null);
      return;
    }
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(i, 0, moved);
    onReorder(next);
    dragIndex.current = null;
    setDragOverIndex(null);
  };

  /**
   * Cleans up drag state when drag ends.
   */
  const handleDragEnd = () => {
    dragIndex.current = null;
    setDragOverIndex(null);
  };

  return (
    <div className={`${styles.list} ${disabled ? styles.disabled : ''}`}>
      {items.map((item, i) => (
        <div
          key={item}
          className={`${styles.item} ${
            dragOverIndex === i ? styles.dragOver : ''
          }`}
          draggable={!disabled}
          onDragStart={() => !disabled && handleDragStart(i)}
          onDragOver={(e) => !disabled && handleDragOver(e, i)}
          onDrop={() => !disabled && handleDrop(i)}
          onDragEnd={handleDragEnd}
        >
          <span className={styles.grip}>
            <Draggable />
          </span>
          <span className={styles.rank}>{i + 1}</span>
          <span className={styles.label}>{item}</span>
        </div>
      ))}
    </div>
  );
}
